import React, { useEffect, useState } from 'react';
import { Button, Card, Space, Tooltip, message } from 'antd';
import { useI18n } from '../../i18n';
import WizardStepBar from '../WizardStepBar';
import ProductsStep from './ProductsStep';
import ServicesStep from './ServicesStep';
import LogisticsStep from './LogisticsStep';
import { isWizardSaveEnabled, getInitialWizardState } from '../../utils/wizardHelpers';

/**
 * Three-step Products → Services → Logistics sourcing wizard
 * (inquiry-module-redesign, Requirements 3, 5, 8) — embedded inline on the
 * Category Details page, below the item's header fields, rather than in a
 * popup modal.
 */
export default function SelectionWizardPanel({
  item,
  supplierProducts = [],
  products = [],
  services = [],
  suppliers = [],
  // In-progress state for this item, held by a parent that outlives this
  // component (App.js) — CategoryDetailPage, and this panel inside it,
  // fully unmount whenever the user follows a Product/Supplier link out to
  // its own page (a real navigation, not a modal) and remount on Back,
  // which would otherwise reset every unsaved selection back to whatever
  // was last actually Saved. `draft` seeds the very next mount; every local
  // change is mirrored back out via `onDraftChange` so it's there to seed
  // the mount after that.
  draft,
  onDraftChange,
  onSave,
  onCancel,
  onOpenProduct,
  onOpenSupplier,
  onAddProduct,
  onCopyProduct,
}) {
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(() => new Set());
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [logisticsNotes, setLogisticsNotes] = useState('');

  // On mount / item change, resume an in-progress draft if there is one for
  // this item, else restore prior (Saved) selections and resume at the last
  // step reached — since Save requires all three steps complete, a saved
  // wizardResult always means every step was already finished, so the "last
  // step reached" is simply Logistics (Requirement 3.4).
  useEffect(() => {
    if (draft) {
      setSelectedProducts(draft.selectedProducts);
      setSelectedServices(draft.selectedServices);
      setLogisticsNotes(draft.logisticsNotes);
      setCompletedSteps(draft.completedSteps);
      setCurrentStep(draft.currentStep);
      return;
    }
    const initial = getInitialWizardState(item);
    setSelectedProducts(initial.selectedProducts);
    setSelectedServices(initial.selectedServices);
    setLogisticsNotes(initial.logisticsNotes);
    setCompletedSteps(initial.completedSteps);
    setCurrentStep(initial.currentStep);
    // Deliberately keyed on the item alone, not `draft` — this effect seeds
    // state on mount, it doesn't react to every subsequent draft echo (see
    // the mirroring effect below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  // Mirror every local change back out to the parent so it survives this
  // component unmounting. Skipped on the very first render (nothing has
  // changed yet, and item may still be null) via the mount-effect ordering.
  useEffect(() => {
    if (!item) return;
    onDraftChange?.(item.id, {
      selectedProducts, selectedServices, logisticsNotes, currentStep, completedSteps,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, selectedProducts, selectedServices, logisticsNotes, currentStep, completedSteps]);

  if (!item) return null;

  const saveEnabled = isWizardSaveEnabled(selectedProducts, selectedServices, logisticsNotes);

  const handleBack = () => setCurrentStep((prev) => Math.max(0, prev - 1));

  const handleNext = () => {
    if (currentStep === 0) {
      if (selectedProducts.length === 0) {
        message.warning(t('Please select at least one product to continue'));
        return;
      }
      setCompletedSteps((prev) => new Set(prev).add(0));
      setCurrentStep(1);
    } else if (currentStep === 1) {
      if (selectedServices.length === 0) {
        message.warning(t('Please select at least one service to continue'));
        return;
      }
      setCompletedSteps((prev) => new Set(prev).add(1));
      setCurrentStep(2);
    }
  };

  const missingStepsMessage = () => {
    const missing = [];
    if (selectedProducts.length === 0) missing.push(t('Products'));
    if (selectedServices.length === 0) missing.push(t('Services'));
    if (!logisticsNotes.trim()) missing.push(t('Logistics'));
    return t('Still needed: {{steps}}', { steps: missing.join(', ') });
  };

  const handleSave = () => {
    if (!saveEnabled) {
      message.warning(missingStepsMessage());
      return;
    }
    try {
      onSave?.(item.id, { selectedProducts, selectedServices, logisticsNotes });
    } catch (err) {
      message.error(t('Could not save selections. Please try again.'));
    }
  };

  return (
    <Card title={t('Sourcing')} style={{ borderRadius: 16 }}>
      <WizardStepBar
        currentStep={currentStep}
        completedSteps={completedSteps}
        onNavigate={setCurrentStep}
      />
      {currentStep === 0 && (
        <ProductsStep
          supplierProducts={supplierProducts}
          products={products}
          categoryId={item.categoryId}
          suppliers={suppliers}
          selectedProducts={selectedProducts}
          onChange={setSelectedProducts}
          onOpenProduct={onOpenProduct}
          onOpenSupplier={onOpenSupplier}
          onAddProduct={onAddProduct}
          onCopyProduct={onCopyProduct}
        />
      )}
      {currentStep === 1 && (
        <ServicesStep
          services={services}
          suppliers={suppliers}
          selectedProducts={selectedProducts}
          selectedServices={selectedServices}
          onChange={setSelectedServices}
          onOpenSupplier={onOpenSupplier}
        />
      )}
      {currentStep === 2 && (
        <LogisticsStep
          logisticsNotes={logisticsNotes}
          onChange={setLogisticsNotes}
        />
      )}
      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          {onCancel && <Button onClick={onCancel}>{t('Cancel')}</Button>}
          {currentStep > 0 && <Button onClick={handleBack}>{t('Back')}</Button>}
          {currentStep < 2 ? (
            <Tooltip title={(currentStep === 0 ? selectedProducts.length === 0 : selectedServices.length === 0)
              ? t(currentStep === 0 ? 'Select at least one product to continue' : 'Select at least one service to continue')
              : ''}
            >
              <span>
                <Button
                  type="primary"
                  disabled={currentStep === 0 ? selectedProducts.length === 0 : selectedServices.length === 0}
                  onClick={handleNext}
                >
                  {t('Next')}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={saveEnabled ? '' : missingStepsMessage()}>
              <span>
                <Button type="primary" disabled={!saveEnabled} onClick={handleSave}>{t('Save')}</Button>
              </span>
            </Tooltip>
          )}
        </Space>
      </div>
    </Card>
  );
}
